/**
 * Token custody for the admin web app.
 *
 * The decision (DECISIONS.md, put to the user before any code): **bearer tokens,
 * no cookies.** The API issues bearer tokens and its CORS is
 * `allow_credentials=False`, so there is no cookie for a browser to send
 * automatically - which means CSRF is structurally absent rather than mitigated.
 * NFR-10.3.6 asks for "CSRF/XSS protections on cookie or token web auth", and
 * this file is the "token web auth" half of that sentence.
 *
 * What that buys and what it costs:
 *
 * - **No CSRF defence is needed** because no credential is ambient. A hostile
 *   page cannot make the browser attach this token to a request it causes.
 * - **XSS is the remaining risk**, and the answer is to keep the access token out
 *   of reach of any persisted storage: it lives in a module variable, so it dies
 *   with the tab and is never written to `localStorage`, `sessionStorage`, or a
 *   URL. The refresh token has to survive a page reload, so it does live in
 *   `sessionStorage` - a deliberate, bounded concession: it is the LONGER-lived
 *   of the two, but it is also the one that is single-use and rotated on every
 *   refresh (spec 02's `rotate_session`), so a stolen copy is good for one use
 *   and is revoked the moment the real client refreshes.
 * - `sessionStorage` rather than `localStorage` so closing the tab ends the
 *   session. An admin tool left open on a shared desk is the threat this closes.
 *
 * The rest of the XSS posture is in `README.md` (the CSP the host must serve)
 * and in the code: nothing here ever touches `innerHTML`, and React escapes
 * everything it renders.
 */

const REFRESH_KEY = "fitzone.admin.refresh";

/** The access token, held in memory only - never persisted. */
let accessToken: string | null = null;

export type AdminIdentity = {
  id: string;
  full_name: string;
  email: string | null;
};

let identity: AdminIdentity | null = null;

/**
 * `sessionStorage` can throw: a browser in private mode, one with site data
 * blocked, or a sandboxed frame refuses access entirely. Every access is guarded
 * so the app degrades to "signed out" rather than failing to boot - the same
 * posture the member app takes with Keychain.
 */
function readStoredRefresh(): string | null {
  try {
    return window.sessionStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function writeStoredRefresh(token: string | null): void {
  try {
    if (token === null) window.sessionStorage.removeItem(REFRESH_KEY);
    else window.sessionStorage.setItem(REFRESH_KEY, token);
  } catch {
    // Nothing to do: the session still works for this page's lifetime, it just
    // will not survive a reload.
  }
}

export const session = {
  getAccessToken: (): string | null => accessToken,
  getRefreshToken: (): string | null => readStoredRefresh(),
  getIdentity: (): AdminIdentity | null => identity,

  /** Record a freshly issued session. */
  start(access: string, refresh: string, admin: AdminIdentity): void {
    accessToken = access;
    identity = admin;
    writeStoredRefresh(refresh);
  },

  /**
   * Record a rotation. The identity is unchanged - `rotate_session` refuses to
   * rotate across roles, so the admin is necessarily the same one.
   */
  rotate(access: string, refresh: string): void {
    accessToken = access;
    writeStoredRefresh(refresh);
  },

  /**
   * Re-adopt the identity on a page RELOAD.
   *
   * The access token and the identity both live in memory, so a reload starts
   * with neither - which is the point (see the module docstring). The refresh
   * response carries `admin`, so restoring the session also restores who the
   * admin is, without a second endpoint to ask.
   */
  setIdentity(admin: AdminIdentity): void {
    identity = admin;
  },

  /** Drop everything. Called on logout AND whenever a refresh fails. */
  clear(): void {
    accessToken = null;
    identity = null;
    writeStoredRefresh(null);
  },
};
